import {
  BuildingOffice2Icon,
  CalendarIcon,
  CreditCardIcon,
  HeartIcon,
  HomeIcon,
} from '@heroicons/react/24/solid';
import React, { useEffect, useMemo, useState, type CSSProperties } from 'react';
import healthcomLogo from '../../assets/healthcom-logo.png';

/**
 * "Slow Dreamy Torus" loading page.
 *
 * Center: HealthCom logo pops in once and breathes.
 *
 * Around it: 5 concentric rings of dots forming a torus, each ring
 * wrapped in its own rotating container so dots physically orbit
 * the center while pulsing in size in place. Adjacent rings rotate
 * in opposite directions for a parallax-depth effect.
 *
 * Color: a desaturated, lightened HSL hue sweep around the circle
 * (red at top → magenta on the right → blue at the bottom →
 * teal/orange on the left), so the torus reads as a soft watercolor
 * band rather than a saturated rainbow.
 *
 * Motion: rotations span 38–68 seconds per revolution and the dot
 * pulse runs on a 3.6-second cycle with a gentle scale/opacity
 * range. Together they read as a meditative breathing wave instead
 * of an active spinner.
 *
 * Icon: a single soft white pill periodically appears at a random
 * middle-ring angle, dwells for ~3 seconds, fades back out, gap,
 * reappears elsewhere with a different nav-section icon.
 */

type RingDef = {
  count: number;
  radius: number;
  size: number;
  rotateSec: number;
  reverse?: boolean;
};

// 5 rings, sized so the middle ring (index 2) is the largest dot —
// gives the torus its visual "body" while the inner / outer rings
// taper down. Rotation periods are intentionally slow (38–68s)
// for the meditative feel that distinguishes this variant.
const RINGS: RingDef[] = [
  { count: 14, radius: 78, size: 3, rotateSec: 60.8 },
  { count: 20, radius: 96, size: 5, rotateSec: 49.4, reverse: true },
  { count: 28, radius: 116, size: 7, rotateSec: 38.0 },
  { count: 34, radius: 136, size: 5, rotateSec: 53.2, reverse: true },
  { count: 40, radius: 156, size: 3, rotateSec: 68.4 },
];

const ICON_RING_INDEX = 2;

// Pulse cycle for the dot ripple — slow and gentle to match the
// dreamy rotation speeds.
const PULSE_CYCLE_MS = 3600;

const ICON_HOLD_MS = 3000;
const ICON_GAP_MIN_MS = 700;
const ICON_GAP_MAX_MS = 1300;

// Pastel hue sweep: low saturation, high lightness so colors look
// washed out / watercolor. Inner and outer rings sit slightly
// flatter than the middle so the saturated middle ring reads as
// the "body" of the band even within this gentle palette.
const hueForAngle = (angle: number): number => {
  const top = (((angle + 90) % 360) + 360) % 360;

  return (360 - top) % 360;
};

type DotDef = {
  key: string;
  x: number;
  y: number;
  size: number;
  color: string;
  delayMs: number;
};

const buildRings = (): DotDef[][] =>
  RINGS.map((ring, ringIdx) => {
    const ringDots: DotDef[] = [];

    for (let i = 0; i < ring.count; i++) {
      const angle = -90 + (360 / ring.count) * i;
      const rad = (angle * Math.PI) / 180;
      const x = Math.cos(rad) * ring.radius;
      const y = Math.sin(rad) * ring.radius;
      const hue = hueForAngle(angle);
      const distFromMid = Math.abs(ringIdx - ICON_RING_INDEX);
      const saturation = 42 - distFromMid * 4;
      const lightness = 76 + distFromMid * 3;
      const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      const phase = (((angle + 90) % 360) + 360) % 360;
      const delayMs = -(phase / 360) * PULSE_CYCLE_MS;

      ringDots.push({ key: `r${ringIdx}-i${i}`, x, y, size: ring.size, color, delayMs });
    }

    return ringDots;
  });

type IconDef = {
  Icon: React.ComponentType<{ className?: string }>;
  color: string;
};

// Mirrors the BottomNav order so the loader doubles as a visual
// preview of the portal's information architecture.
const ICONS: IconDef[] = [
  { Icon: HomeIcon, color: 'text-emerald-500' },
  { Icon: HeartIcon, color: 'text-rose-500' },
  { Icon: CalendarIcon, color: 'text-indigo-500' },
  { Icon: CreditCardIcon, color: 'text-amber-500' },
  { Icon: BuildingOffice2Icon, color: 'text-violet-500' },
];

// Pre-computed icon orbit positions, one per dot in the icon ring.
const ICON_POSITIONS = (() => {
  const ring = RINGS[ICON_RING_INDEX]!;
  const positions: { x: number; y: number }[] = [];

  for (let i = 0; i < ring.count; i++) {
    const angle = -90 + (360 / ring.count) * i;
    const rad = (angle * Math.PI) / 180;
    positions.push({
      x: Math.cos(rad) * ring.radius,
      y: Math.sin(rad) * ring.radius,
    });
  }

  return positions;
})();

type ActiveIcon = {
  iconIdx: number;
  posIdx: number;
  visible: boolean;
  key: number;
};

const HealthcomLoadingPage: React.FC = () => {
  const ringDots = useMemo(buildRings, []);
  const [active, setActive] = useState<ActiveIcon | null>(null);

  useEffect(() => {
    let keyCounter = 0;
    let canceled = false;
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const cycle = () => {
      if (canceled) return;
      const iconIdx = Math.floor(Math.random() * ICONS.length);
      const posIdx = Math.floor(Math.random() * ICON_POSITIONS.length);
      const key = ++keyCounter;

      // Mount invisible so the CSS transition has a real "from"
      // state to animate from on the next paint.
      setActive({ iconIdx, posIdx, visible: false, key });

      timeouts.push(
        setTimeout(() => {
          if (canceled) return;
          setActive(a => (a && a.key === key ? { ...a, visible: true } : a));
        }, 30)
      );
      timeouts.push(
        setTimeout(() => {
          if (canceled) return;
          setActive(a => (a && a.key === key ? { ...a, visible: false } : a));
        }, ICON_HOLD_MS)
      );
      timeouts.push(
        setTimeout(() => {
          if (canceled) return;
          setActive(a => (a && a.key === key ? null : a));
          const gap = ICON_GAP_MIN_MS + Math.random() * (ICON_GAP_MAX_MS - ICON_GAP_MIN_MS);
          timeouts.push(setTimeout(cycle, gap));
        }, ICON_HOLD_MS + 700)
      );
    };

    timeouts.push(setTimeout(cycle, 700));

    return () => {
      canceled = true;
      timeouts.forEach(clearTimeout);
    };
  }, []);

  return (
    <div
      className="min-h-dvh flex items-center justify-center px-4 antialiased overflow-hidden bg-white dark:bg-gradient-to-br dark:from-zinc-950 dark:to-zinc-900"
      // CSS custom properties drive the dot pulse range consumed by
      // the `hc-dot-pulse` keyframe (see index.css). Tuned softer
      // than the keyframe defaults to match the meditative palette.
      style={
        {
          '--hc-pulse-scale-min': 0.78,
          '--hc-pulse-scale-max': 1.16,
          '--hc-pulse-opacity-min': 0.5,
          '--hc-pulse-opacity-max': 0.88,
          '--hc-pulse-cycle': `${PULSE_CYCLE_MS}ms`,
        } as CSSProperties
      }
    >
      <div className="relative size-[360px] sm:size-[420px]">
        {/* Dot rings — each ring rotates as a unit so dots
            physically orbit the center while pulsing in place. */}
        {ringDots.map((dots, ringIdx) => {
          const ring = RINGS[ringIdx]!;

          return (
            <div
              key={ringIdx}
              className={`absolute inset-0 ${
                ring.reverse ? 'hc-ring-rotate-ccw' : 'hc-ring-rotate-cw'
              }`}
              style={
                {
                  '--hc-ring-duration': `${ring.rotateSec}s`,
                } as CSSProperties
              }
              aria-hidden="true"
            >
              {dots.map(dot => (
                <div
                  key={dot.key}
                  className="absolute top-1/2 left-1/2"
                  style={{
                    transform: `translate(calc(-50% + ${dot.x}px), calc(-50% + ${dot.y}px))`,
                  }}
                >
                  <span
                    className="hc-dot block rounded-full"
                    style={{
                      width: `${dot.size}px`,
                      height: `${dot.size}px`,
                      backgroundColor: dot.color,
                      animationDelay: `${dot.delayMs}ms`,
                    }}
                  />
                </div>
              ))}
            </div>
          );
        })}

        {/* Center logo pill — Apple-style rounded-square app icon. */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="hc-logo-pop size-24 sm:size-28 rounded-[1.5rem] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.12)] ring-1 ring-black/5 flex items-center justify-center">
            <img
              src={healthcomLogo}
              alt="HealthCom"
              className="h-14 sm:h-16 w-auto object-contain"
              draggable={false}
            />
          </div>
        </div>

        {/* Icon overlay — single soft white chip that periodically
            appears at a random middle-ring angle. */}
        {active &&
          (() => {
            const pos = ICON_POSITIONS[active.posIdx]!;
            const { Icon, color } = ICONS[active.iconIdx]!;

            return (
              <div
                key={active.key}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{
                  transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))`,
                }}
                aria-hidden="true"
              >
                <div
                  className="hc-icon-bubble size-12 sm:size-14 rounded-full bg-white shadow-[0_4px_16px_rgba(0,0,0,0.12)] ring-1 ring-black/5 flex items-center justify-center"
                  data-visible={active.visible ? 'true' : 'false'}
                >
                  <Icon className={`size-6 sm:size-7 ${color} shrink-0`} />
                </div>
              </div>
            );
          })()}
      </div>

      <span className="sr-only" role="status" aria-live="polite">
        Loading HealthCom…
      </span>
    </div>
  );
};

export default HealthcomLoadingPage;
